import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Modal, Select } from 'antd';
import debounce from 'lodash.debounce';
import { useUpdateTripStatus } from 'api/trips/use-update-trip-status.ts';
import { TransportType } from 'types/transport';
import { TransportAvailabilityStatus } from 'components/transport/transport-availability-status';
import type { IStatusOption, ITripStatusEditModalProps, ITripStatusFormFields } from './types';

export const TripStatusEditModal: React.FC<ITripStatusEditModalProps> = (props) => {
  const { isOpen, onClose, trip } = props;
  const { t } = useTranslation('trip');

  const { mutateAsync: updateStatus, isLoading } = useUpdateTripStatus(trip.id);

  const [form] = Form.useForm();
  const handleOk = debounce(() => form.submit(), 200);

  const onFinish = async (values: ITripStatusFormFields) => {
    await updateStatus(values);
    onClose();
  };

  const statuses: IStatusOption[] = [
    { value: 'planning', label: t('status_planning') },
    { value: 'ready', label: t('status_ready') },
    { value: 'progress', label: t('status_progress') },
    { value: 'done', label: t('status_done') },
  ];

  const preparedStatuses: IStatusOption[] = statuses.map((status, index) => {
    if (
      (statuses[index - 1]?.value === trip.status || statuses[index + 1]?.value === trip.status) &&
      trip.status !== 'done'
    ) {
      return { ...status };
    }

    return { ...status, disabled: true };
  });

  return (
    <Modal title={t('edit_status')} open={isOpen} confirmLoading={isLoading} onCancel={onClose} onOk={handleOk}>
      <Form
        form={form}
        name='trip_status'
        layout='vertical'
        initialValues={trip.status ? { status: trip.status } : undefined}
        onFinish={onFinish}>
        <Form.Item name='status' rules={[{ required: true, type: 'string' }]} style={{ marginBottom: '16px' }}>
          <Select placeholder={t('status')} size={'large'} options={preparedStatuses} />
        </Form.Item>

        <TransportAvailabilityStatus
          transportId={trip.truckId}
          status={trip.status}
          transportType={TransportType.truck}
        />
        <TransportAvailabilityStatus
          transportId={trip.trailerId}
          status={trip.status}
          transportType={TransportType.trailer}
        />
      </Form>
    </Modal>
  );
};
